import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Sheet,
    Typography,
    Button,
    List,
    ListItem,
    ListItemDecorator,
    CircularProgress,
    Alert,
    Stack,
    AspectRatio
} from '@mui/joy';
import VideocamIcon from '@mui/icons-material/Videocam';
import MicIcon from '@mui/icons-material/Mic';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ImageCapture from '../components/ImageCapture';
import domain from '../utils/domain';
import { Student, Exam, Registration } from '../types/exam';

const Proctoring: React.FC = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<{
        registration: Registration;
        student: Student;
        exam: Exam;
    } | null>(null);
    const { examId, registrationHash } = useParams<{ examId: string; registrationHash: string }>();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [hasCamera, setHasCamera] = useState(false);
    const [hasMicrophone, setHasMicrophone] = useState(false);
    const [isBrowserCheckStarted, setIsBrowserCheckStarted] = useState(false);
    const [deviceError, setDeviceError] = useState<string>('');

    useEffect(() => {
        // Load data from localstorage
        const studentData = localStorage.getItem('studentData');
        console.log("Student Data", studentData);
        if (studentData) {
            setData(JSON.parse(studentData));
            setIsLoading(false);
        } else {
            fetchExamDetails();
        }
    }, []);

    useEffect(() => {
        // Disable right-click
        const handleContextMenu = (e: MouseEvent) => {
            e.preventDefault();
            return false;
        };

        // Disable keyboard shortcuts
        const handleKeyDown = (e: KeyboardEvent) => {
            // Prevent Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+Shift+C, F12
            if (
                (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i' || e.key === 'J' || e.key === 'j' || e.key === 'C' || e.key === 'c')) ||
                e.key === 'F12'
            ) {
                e.preventDefault();
                return false;
            }
        };

        // Clear console and disable console methods
        const disableConsole = () => {
            console.clear();
            const noop = () => undefined;
            // Store original console methods
            const originalConsole = { ...console };

            // Override console methods
            Object.keys(console).forEach((key) => {
                (console as any)[key] = noop;
            });

            // Periodically check and re-disable if console is re-enabled
            setInterval(() => {
                if ((console as any).clear !== noop) {
                    Object.keys(console).forEach((key) => {
                        (console as any)[key] = noop;
                    });
                }
            }, 1000);
        };

        // Add event listeners
        document.addEventListener('contextmenu', handleContextMenu);
        document.addEventListener('keydown', handleKeyDown);

        // Initial disable
        disableConsole();

        // Cleanup
        return () => {
            document.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const fetchExamDetails = async () => {
        try {
            const response = await fetch(`${domain}/api/registrations/${examId}/${registrationHash}`);
            if (!response.ok) {
                throw new Error('Failed to fetch exam details');
            }
            const data = await response.json();
            setData(data);
            setIsLoading(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load exam details');
            setIsLoading(false);
        }
    };

    const handleCameraStatus = (success: boolean) => {
        setHasCamera(success);
        setHasMicrophone(success); // We'll assume microphone is available if camera works
        setIsBrowserCheckStarted(true);
    };

    const handleProceedToExam = () => {
        navigate(`/exam/instructions/${examId}/${registrationHash}`);
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress size="lg" />
            </Box>
        );
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            bgcolor: 'white',
            py: 4,
            px: 2
        }}>
            <Sheet
                variant="outlined"
                sx={{
                    maxWidth: '800px',
                    margin: '0 auto',
                    borderRadius: 'lg',
                    overflow: 'hidden',
                    borderColor: '#ea7a23'
                }}
            >
                {/* Header with Logo */}
                <Box sx={{
                    bgcolor: 'white',
                    p: 3,
                    height: '180px',
                    borderBottom: '1px solid',
                    borderColor: '#ea7a23',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                }}>
                    <AspectRatio
                        ratio="3/1"
                        sx={{
                            width: 300,
                            margin: '0 auto',
                            mb: 2
                        }}
                    >
                        <img
                            src={`${process.env.PUBLIC_URL}/logo-vector.svg`}
                            alt="Vector India Logo"
                            style={{ objectFit: 'contain' }}
                        />
                    </AspectRatio>
                    <Typography
                        level="h2"
                        sx={{
                            textAlign: 'center',
                            color: '#ea7a23',
                            fontSize: '1.75rem'
                        }}
                    >
                        Exam Proctoring Setup
                    </Typography>
                </Box>

                <Box sx={{ p: 4 }}>
                    <Typography level="title-lg" sx={{ mb: 2, color: '#ea7a23' }}>
                        Important Instructions:
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemDecorator>1.</ListItemDecorator>
                            Camera and microphone access is mandatory for this exam
                        </ListItem>
                        <ListItem>
                            <ListItemDecorator>2.</ListItemDecorator>
                            Ensure you are in a quiet, well-lit environment
                        </ListItem>
                        <ListItem>
                            <ListItemDecorator>3.</ListItemDecorator>
                            Your face should be clearly visible in the camera
                        </ListItem>
                        <ListItem>
                            <ListItemDecorator>4.</ListItemDecorator>
                            The exam will be monitored for any suspicious activity
                        </ListItem>
                        <ListItem>
                            <ListItemDecorator>5.</ListItemDecorator>
                            Disabling camera or microphone during the exam will result in warnings
                        </ListItem>
                    </List>

                    {deviceError && (
                        <Alert
                            color="danger"
                            variant="soft"
                            sx={{ mt: 2 }}
                            startDecorator={<ErrorIcon />}
                        >
                            {deviceError}
                        </Alert>
                    )}

                    <Box sx={{ mt: 4 }}>
                        <ImageCapture
                            title="Proctoring Camera Setup"
                            onCapture={handleCameraStatus}
                        />

                        {isBrowserCheckStarted && (
                            <Stack spacing={2} sx={{ mt: 3 }}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    color: hasCamera ? 'success.500' : 'danger.500'
                                }}>
                                    <VideocamIcon />
                                    <Typography>
                                        Camera: {hasCamera ? 'Available' : 'Not Available'}
                                    </Typography>
                                    {hasCamera ? <CheckCircleIcon /> : <ErrorIcon />}
                                </Box>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    color: hasMicrophone ? 'success.500' : 'danger.500'
                                }}>
                                    <MicIcon />
                                    <Typography>
                                        Microphone: {hasMicrophone ? 'Available' : 'Not Available'}
                                    </Typography>
                                    {hasMicrophone ? <CheckCircleIcon /> : <ErrorIcon />}
                                </Box>
                            </Stack>
                        )}
                    </Box>

                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 2,
                        mt: 4
                    }}>
                        <Button
                            size="lg"
                            onClick={handleProceedToExam}
                            endDecorator={<ArrowForwardIcon />}
                            disabled={!hasCamera || !hasMicrophone}
                            sx={{
                                bgcolor: '#ea7a23',
                                '&:hover': { bgcolor: '#d86a1f' }
                            }}
                        >
                            Proceed to Next
                        </Button>
                    </Box>
                </Box>
            </Sheet>
        </Box>
    );
};

export default Proctoring; 